'use client';

import LoadingButton from '@mui/lab/LoadingButton';
import { Stack, Avatar, Typography, Button, Link, Collapse, Divider } from '@mui/material';
import { AnimatePresence, m } from 'framer-motion';
import { isEmpty, startCase } from 'lodash';
import { useQueryState } from 'nuqs';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSendParticipation } from 'src/api/connect';
import { useGetIsSubscribed, useSubscribeToOrganizer } from 'src/api/hooks/organizer';
import { useAuthContext } from 'src/auth/hooks';
import EventPriceCard from 'src/components/event-price-card/event-price-card';
import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';
import { useTranslate } from 'src/locales';

import { useRouter } from '../../routes/hooks';
import {
  CartItemDTO,
  CartShopItemDTO,
  EventType,
  IEvent,
  IOrganization,
  IOrganizer,
  SubEntityType,
  SubscriptionType,
  User,
} from '../../types';
import { getPictureToDisplay } from '../../utils/picture';
import { CreateProfileDialog } from './create-profile';
import EventDetailsHeader from './event-details-header';
import MessageDisplay from './event-order-status';
import EventPromoCode from './event-promocode';
import { getButtonLabel } from './event-utils';
import { useTicketCalculations } from './useTicketCalculations';

const BOT_ID = 1;

interface FormValues {
  tickets: CartItemDTO[];
  shopItems: CartShopItemDTO[];
}

interface RenderSubmitterProps {
  organizer?: IOrganizer;
  alreadySubscribed: boolean;
  organization?: IOrganization;
  user: User;
  isDark: boolean;
  eventId?: number;
  handleSubscribe: () => void;
}

const RenderSubmitter = ({
  organizer,
  alreadySubscribed,
  organization,
  user,
  isDark,
  eventId,
  handleSubscribe,
}: RenderSubmitterProps) => {
  const { t } = useTranslate('common');
  if (organizer?.name) {
    return (
      <Stack spacing={2} direction="row" alignItems="center">
        <Avatar src={organizer?.pictureURL} sx={{ width: 48, height: 48 }} />
        <Stack direction="column" spacing={2} alignItems="flex-start">
          <Typography>{startCase(organizer?.name)}</Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar
          src={organization?.logo?.url ?? getPictureToDisplay(user)}
          sx={{ width: 48, height: 48 }}
        />
        <Stack direction="column" alignItems="flex-start">
          <Link href={organization?.normalizedName ? `/${organization?.normalizedName}` : ''}>
            <Typography>{startCase(organization?.name ?? user?.firstname)}</Typography>
          </Link>
          {eventId === 11707 && <Typography>By The Neighbors & Jamdepaname</Typography>}
        </Stack>
      </Stack>
      <Button
        variant="outlined"
        sx={{
          borderRadius: 1,
          p: 1.3,
          width: '7rem',
          color: isDark ? '#000' : '#fff',
        }}
        onClick={handleSubscribe}
      >
        {alreadySubscribed ? t('buttons.following') : t('buttons.follow')}
      </Button>
    </Stack>
  );
};

type Props = {
  event: IEvent;
};

const EventDetailsDesktopHeader = ({ event }: Props) => {
  const {
    id,
    endDate,
    type,
    user,
    organization,
    organizer,
    cartId,
    userOrders,
    hasPendingRequest,
  } = event;

  const eventId = id;
  const eventDate = new Date(endDate ?? '');
  const { authenticated, user: currentUser } = useAuthContext();
  const router = useRouter();
  const { t } = useTranslate('common');
  const { t: tEvent } = useTranslate('event');
  const { t: tConnect } = useTranslate('connect');
  const [__, setOpenLogin] = useQueryState('login');
  const [____, setReturnTo] = useQueryState('returnTo');

  const [_, setCheckout] = useQueryState('checkout');

  const methods = useFormContext<FormValues>();
  const showCreateProfileDialog = useBoolean();
  const checkoutModal = useBoolean();
  const { sendParticipation } = useSendParticipation();

  const currentSubscriberEmail =
    (currentUser?.email as string) ||
    (typeof window !== 'undefined' && localStorage.getItem('subscribed_email')) ||
    '';

  const { data: alreadySubscribed } = useGetIsSubscribed(
    currentSubscriberEmail ?? '',
    organization?.normalizedName || ''
  );

  const { mutateAsync: subscribe } = useSubscribeToOrganizer();

  const {
    watch,
    getValues,
    formState: { isDirty, isSubmitting },
  } = methods;

  const tickets = watch('tickets');
  const shopItemsList = watch('shopItems');
  const [___, setIsExclusiveEvent] = useQueryState('exclusive');

  const {
    serviceFees,
    isFreeTicket,
    promoCode,
    promoCodeError,
    discountedTotalCost,
    isUpdating,
    appliedDiscount,
    hasTicketWithQuantityMoreThanZero,
  } = useTicketCalculations({
    tickets,
    shopItemsList,
    getValues,
    eventId,
  });

  const hasAnOrder = userOrders?.some((order) => order.id > 0);

  const handleClickOnConnect = useCallback(() => {
    if (!authenticated) {
      setOpenLogin(`open`);
      setReturnTo(`https://trybe.fm/event/${id}/attendees`);
      return null;
    }
    if (event?.type === EventType.EXCLUSIVE && isEmpty(event?.tickets)) {
      setIsExclusiveEvent('open');
      return null;
    }
    sendParticipation(id?.toString(), { connect: true });
    router.push(`attendees`);

    return null;
  }, [
    authenticated,
    id,
    event,
    setReturnTo,
    setIsExclusiveEvent,
    router,
    sendParticipation,
    setOpenLogin,
  ]);

  const handleCheckout = useCallback(() => {
    if (!authenticated) {
      setOpenLogin('open');
      return null;
    }
    setCheckout('true');
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, checkoutModal, router]);

  const handleSubscribe = useCallback(async () => {
    if (authenticated && currentUser) {
      await subscribe({
        email: currentUser?.email,
        entityId: event.organization?.id || 0,
        entityType: SubEntityType.Organization,
        subscriptionType: SubscriptionType.Organizer,
        locations: [{ placeId: 'ChIJW6T5-yMbyUwRLWIrtNTBSaw' }],
      });
    } else {
      router.push(`/${organization?.normalizedName}/?openSubscribe=true`);
    }
  }, [
    authenticated,
    currentUser,
    subscribe,
    event.organization?.id,
    router,
    organization?.normalizedName,
  ]);

  const isDark = !event?.configuration?.displayMode
    ? false
    : event?.configuration?.displayMode !== 'DARK';
  return (
    <Stack
      sx={{
        position: 'sticky',
        top: 90,
        maxHeight: '85vh',
        width: '35vw',
      }}
      gap={1}
    >
      <AnimatePresence>
        <m.div
          key="main-section"
          initial={{ height: '40vh' }}
          animate={{
            height:
              (isDirty || Boolean(cartId)) && hasTicketWithQuantityMoreThanZero ? '20vh' : '40vh',
          }}
          exit={{ height: '40vh' }} // Height when component unmounts
          transition={{ duration: 0.5 }} // Smooth transition duration
        >
          <EventDetailsHeader event={event} />
        </m.div>
        <MessageDisplay
          hasPendingRequest={hasPendingRequest}
          isDirty={isDirty}
          hasAnOrder={hasAnOrder}
          userOrders={userOrders}
        />
        <m.div
          key="main-header"
          initial={{ minHeight: 0 }} // Starting from 0px
          animate={{
            minHeight:
              (isDirty || Boolean(cartId)) && hasTicketWithQuantityMoreThanZero
                ? 'max-content' // You can also use a specific height like '100vh' if needed
                : '0vh',
          }}
          transition={{ duration: 0.5 }}
        >
          <Collapse in={(isDirty || Boolean(cartId)) && hasTicketWithQuantityMoreThanZero}>
            <Stack
              sx={{
                backgroundColor: '#000',
                paddingTop: 2,
                paddingBottom: 2,
                px: 3,
                borderRadius: 3,
              }}
            >
              {!isFreeTicket && (
                <EventPromoCode
                  appliedPromoCode={promoCode}
                  error={promoCodeError}
                  isLoading={isUpdating}
                />
              )}

              {event?.type === EventType.EXCLUSIVE && (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={2}
                  sx={{ border: '1px solid primary', my: 2, color: 'primary' }}
                >
                  <Iconify icon="solar:info-square-outline" />
                  <Typography>{tEvent('subject-to-approval')}</Typography>
                </Stack>
              )}
              <EventPriceCard
                discount={appliedDiscount}
                appliedPromoCode={promoCode}
                fees={serviceFees}
                isFreeTicket={isFreeTicket}
                currency={event?.currency}
                total={discountedTotalCost}
                isLoading={isUpdating}
              />
              {isEmpty(shopItemsList) ? (
                <LoadingButton
                  variant="contained"
                  aria-label="scroll back to top"
                  size="large"
                  type="submit"
                  sx={{
                    borderRadius: 1,
                  }}
                  disabled={hasPendingRequest || isUpdating}
                  loading={isSubmitting || isUpdating}
                >
                  {getButtonLabel({
                    hasPendingRequest,
                    type,
                    hasShopItem: !isEmpty(shopItemsList),
                    isNotCheckoutModal: true,
                    t,
                  })}
                </LoadingButton>
              ) : (
                <LoadingButton
                  variant="contained"
                  aria-label="scroll back to top"
                  size="large"
                  disabled={hasPendingRequest || isUpdating}
                  sx={{
                    borderRadius: 1,
                  }}
                  loading={isSubmitting || isUpdating}
                  onClick={() => handleCheckout()}
                >
                  {getButtonLabel({
                    hasPendingRequest,
                    type,
                    hasShopItem: !isEmpty(shopItemsList),
                    isNotCheckoutModal: true,
                    t,
                  })}
                </LoadingButton>
              )}
            </Stack>
          </Collapse>
        </m.div>
      </AnimatePresence>
      <Divider sx={{ width: '70%', alignSelf: 'center' }} />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          p: 2,

          backgroundColor: 'rgba(34, 34, 34, 0.2)', // Transparent white background
          borderRadius: '16px', // Rounded corners
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)', // Subtle shadow
          backdropFilter: 'blur(5px)', // Blur effect for glassmorphism
          WebkitBackdropFilter: 'blur(5px)', // Safari support
          border: '1px solid rgba(255, 255, 255, 0.3)', // Semi-transparent border
        }}
      >
        <Stack>
          <Typography variant="h6" color={isDark ? '#000' : '#fff'}>
            {tConnect('description.going-alone')}
          </Typography>
          <Typography color={isDark ? '#000' : '#a7a7a7'} variant="body2">
            {tConnect('description.connect-partner')}
          </Typography>
        </Stack>
        {eventDate > new Date() && (
          <LoadingButton
            loading={false}
            variant="contained"
            aria-label="scroll back to top"
            sx={{
              borderRadius: 1,
              p: 1.3,
              zIndex: 99,
              width: '7rem',
            }}
            onClick={() => handleClickOnConnect()}
          >
            {t('buttons.connect')}
          </LoadingButton>
        )}
      </Stack>
      <Divider sx={{ width: '70%', alignSelf: 'center' }} />
      <Stack
        gap={1}
        sx={{
          p: 2,
          backgroundColor: 'rgba(34, 34, 34, 0.2)', // Transparent white background
          borderRadius: '16px', // Rounded corners
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)', // Subtle shadow
          backdropFilter: 'blur(5px)', // Blur effect for glassmorphism
          WebkitBackdropFilter: 'blur(5px)', // Safari support
          border: '1px solid rgba(255, 255, 255, 0.3)', // Semi-transparent border
        }}
      >
        <Stack>
          <Typography variant="h6" color={isDark ? '#000' : '#fff'}>
            {tEvent('organized-by')}
          </Typography>
        </Stack>
        {(organizer || user?.id !== BOT_ID) && (
          <RenderSubmitter
            organizer={organizer}
            alreadySubscribed={alreadySubscribed ?? false}
            organization={organization}
            user={user}
            isDark={isDark}
            eventId={eventId}
            handleSubscribe={handleSubscribe}
          />
        )}
      </Stack>

      <CreateProfileDialog
        open={showCreateProfileDialog.value}
        onClose={() => showCreateProfileDialog.onFalse()}
      />
    </Stack>
  );
};

export default EventDetailsDesktopHeader;
